/**
 * Decodes HTML entities in a string.
 * Handles common entities like &amp; &lt; &gt; &quot; &#x27; &#39; etc.
 *
 * This is used to decode AI responses that may contain HTML-encoded characters.
 * The approach uses a textarea element which leverages the browser's built-in
 * HTML decoding without executing any scripts (safe from XSS).
 *
 * @param text - The text containing HTML entities to decode
 * @returns The decoded text with entities converted to characters
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return text;

  // Use a textarea element to leverage browser's HTML decoding
  // This is safe because textarea.value returns decoded text, not HTML
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Fully decodes multi-level HTML entity encoding.
 * Handles cases where entities have been encoded multiple times,
 * e.g. &amp;amp;amp;lt; → &amp;amp;lt; → &amp;lt; → &lt; → <
 *
 * @param text - The text that may contain nested HTML entities
 * @returns The fully decoded text
 */
export function fullyDecodeHtmlEntities(text: string): string {
  if (!text || typeof text !== 'string') return text;

  // Quick check: if no entity-like patterns, return as-is
  if (!text.includes('&')) return text;

  const textarea = document.createElement('textarea');
  let decoded = text;
  let prev: string;
  // Safety limit to prevent infinite loops
  let iterations = 0;
  do {
    prev = decoded;
    textarea.innerHTML = decoded;
    decoded = textarea.value;
    iterations++;
  } while (decoded !== prev && iterations < 100);

  return decoded;
}
